import * as React from 'react';
import type { DrawerProps } from '@fluentui/react-components';
import { makeStyles, OverlayDrawer } from '@fluentui/react-components';
import { useMediaQuery } from '@react-hook/media-query';

interface DrawerContext {
  showDrawer: boolean;
  setShowDrawer: (show: boolean | ((shown: boolean) => boolean)) => void;
  children: React.ReactNode | null;
  setChildren: (Component: React.ReactNode | null) => void;
}

export const DrawerContext = React.createContext<DrawerContext>({
  showDrawer: false,
  setShowDrawer: () => {},
  setChildren: () => {},
  children: null,
});

export const SidecarContext = React.createContext<{ isOpen: boolean; setIsOpen: (arg1: boolean) => void }>({
  isOpen: false,
  setIsOpen: () => {},
});

export function DrawerProvider({ children }: { children: React.ReactNode }) {
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [drawerChildren, setChildren] = React.useState<React.ReactNode>(null);
  const [sideCarOpen, setSideCarOpen] = React.useState(false);

  function setSideCarOpenWithDrawer(open: boolean) {
    setSideCarOpen(open);
    setShowDrawer(false);
  }

  return (
    <DrawerContext.Provider
      value={{
        showDrawer,
        setShowDrawer,
        setChildren,
        children: drawerChildren,
      }}
    >
      <SidecarContext.Provider
        value={{
          isOpen: sideCarOpen,
          setIsOpen: setSideCarOpenWithDrawer,
        }}
      >
        {children}
      </SidecarContext.Provider>
    </DrawerContext.Provider>
  );
}

export function FoundersHubUserOverlayDrawer(props: DrawerProps) {
  const classes = useStyles();
  const isSmallScreen = useMediaQuery('screen and (max-width: 430px)');
  const [topOffset, setTopOffset] = React.useState(0);

  const handleScroll = () => {
    if (document.getElementsByTagName('header').length === 0) {
      setTopOffset(0);
      return;
    }

    const headerHeight = document.getElementsByTagName('header')[0].clientHeight ?? 0;
    const headerTopY = document.getElementsByTagName('header')[0].getBoundingClientRect().top ?? 0;
    const offset = headerTopY > -1 * headerHeight ? headerTopY + headerHeight : 0;
    setTopOffset(offset);
  };

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const responsiveWidth = isSmallScreen ? '100%' : '420px';
  const { children, showDrawer } = React.useContext(DrawerContext);

  return (
    <OverlayDrawer
      position="end"
      open={showDrawer}
      style={{
        width: 'auto',
        minWidth: responsiveWidth,
        top: `${topOffset}px`,
        height: `calc(100vh - ${topOffset}px)`,
      }}
      className={classes.drawer}
      backdrop={{ className: classes.backdrop, style: { top: `${topOffset}px` } }}
      {...props}
    >
      {children}
    </OverlayDrawer>
  );
}

const useStyles = makeStyles({
  drawer: {
    zIndex: 1e14,
  },
  backdrop: {
    position: 'fixed',
    zIndex: 1e13,
  },
});
